import {
  type BarChartData,
  calculateThresholdStepValue,
  formatValue,
  getOverriddenProperties,
  type ModeOption,
  niceNum,
  type SeriesOverrides,
  type ThresholdColorPalette,
  type ThresholdOptions,
  type UnitOptions,
} from '@/core';
import { DEFAULT_NO_DATA_MESSAGE } from '../utils';
import type { TopListTooltipData } from './model';

/**
 * Helper to return the correct bar color considering overrides and thresholds.
 */
export const getBarColor = ({
  itemStyleParams,
  overrides,
  primaryColor,
  thresholds,
  thresholdColors,
  maxValue,
}: {
  itemStyleParams: { name: string; color: string; value: number };
  overrides?: SeriesOverrides;
  primaryColor?: string;
  thresholds?: ThresholdOptions;
  thresholdColors: ThresholdColorPalette;
  maxValue?: number;
}) => {
  // Determine if thresholds affect the color
  if (thresholds?.steps?.length) {
    const thresholdColor = determineColorByThreshold({
      value: itemStyleParams.value,
      thresholds,
      thresholdColors,
      maxValue,
    });
    if (thresholdColor) {
      return thresholdColor;
    }
  }

  // Color overrides resolve after thresholds
  const overriddenProperties = getOverriddenProperties(itemStyleParams.name, overrides);
  if (overriddenProperties.color) {
    return overriddenProperties.color;
  }

  return primaryColor || itemStyleParams.color || thresholdColors.defaultColor;
};
/**
 * When threshold steps are defined, bar color is dynamic based on whether its value exceeds the threshold.
 */
function determineColorByThreshold({
  value,
  thresholds,
  thresholdColors,
  maxValue,
}: {
  value: number;
  thresholds?: ThresholdOptions;
  thresholdColors: ThresholdColorPalette;
  maxValue?: number;
}): string | undefined {
  if (!thresholds?.steps || thresholds.steps.length === 0) return undefined;

  // Loop through thresholds backwards since they are in ascending order and we can early return when data value exceeds step value
  for (let i = thresholds.steps.length - 1; i >= 0; i--) {
    const step = thresholds.steps[i];
    if (!step) continue;
    const thresholdValue = calculateThresholdStepValue(step, thresholds.mode, maxValue);
    if (value >= thresholdValue) {
      const stepPaletteColor = thresholdColors.palette[i] ?? thresholdColors.defaultColor;
      return step.color ?? stepPaletteColor; // Fallback to charts theme color if step color not explicitly defined
    }
  }

  return undefined;
}

/**
 * Get the bar chart tooltip data for the given data index.
 */
export function getTooltipData({
  data,
  dataIndex,
  overrides,
  primaryColor,
  defaultColor = '',
  thresholds,
  thresholdColors,
  maxValue,
}: {
  data: BarChartData[];
  dataIndex: number;
  overrides?: SeriesOverrides;
  primaryColor?: string;
  /** Fallback color when primaryColor and overrides are not set (e.g. from ECharts theme palette). */
  defaultColor?: string;
  thresholds?: ThresholdOptions;
  thresholdColors: ThresholdColorPalette;
  maxValue?: number;
}): TopListTooltipData | undefined {
  const datum = data?.[dataIndex];
  if (!datum) return;
  const getColor = (value: number | null | undefined): string => {
    if (value === undefined || value === null) return '';
    return getBarColor({
      itemStyleParams: {
        name: datum.label,
        value,
        color: defaultColor,
      },
      overrides,
      primaryColor,
      thresholds,
      thresholdColors,
      maxValue,
    });
  };
  return {
    label: datum.label,
    value: datum.value,
    comparisonValue: datum.comparisonValue,
    color: getColor(datum.value),
    comparisonColor: getColor(datum.comparisonValue),
  };
}

/**
 * Account for percentage mode when formatting value, used in tooltip content.
 */
export function formatDisplayValue({
  value,
  mode,
  unit,
}: {
  value: number | undefined;
  mode: ModeOption;
  unit: UnitOptions;
}) {
  if (value === undefined) return DEFAULT_NO_DATA_MESSAGE;
  if (mode === 'percentage') {
    return formatValue(value, {
      ...unit,
      kind: 'Percent',
    });
  }
  return formatValue(value, unit);
}

interface XAxisTick {
  value: number;
  label: string;
}

/**
 * Compute "nice" x-axis tick values and formatted labels for the bar chart.
 * Generates approximately `desiredTickCount` ticks across the data range.
 * The axis always includes zero so bars can anchor at the origin.
 * Adds ~10% boundary gap to match ECharts `boundaryGap: [0, '10%']`.
 */
export function computeXAxisTicks({
  maxValue,
  minValue = 0,
  unit,
  mode,
  desiredTickCount = 5,
}: {
  maxValue: number;
  /** Minimum data value; when negative the axis extends left of zero. */
  minValue?: number;
  unit: UnitOptions;
  mode: ModeOption;
  desiredTickCount?: number;
}): { ticks: XAxisTick[]; axisMin: number; axisMax: number } {
  // Axis always includes zero so bars can grow from the origin.
  const effectiveMin = Math.min(0, minValue);
  const effectiveMax = Math.max(0, maxValue);

  if (effectiveMin === 0 && effectiveMax === 0) {
    return { ticks: [], axisMin: 0, axisMax: 0 };
  }
  if (!Number.isFinite(effectiveMin) || !Number.isFinite(effectiveMax)) {
    return { ticks: [], axisMin: 0, axisMax: 0 };
  }

  // All-positive path (preserves existing behaviour)
  if (effectiveMin >= 0) {
    const paddedMax = effectiveMax * 1.1;
    const step = niceNum(paddedMax / desiredTickCount);
    const niceMax = Math.ceil(paddedMax / step) * step;

    const ticks: XAxisTick[] = [];
    const tickCount = Math.round(niceMax / step);
    for (let i = 0; i <= tickCount; i++) {
      const val = i * step;
      ticks.push({ value: val, label: formatDisplayValue({ value: val, mode, unit }) });
    }
    return { ticks, axisMin: 0, axisMax: niceMax };
  }

  // Negative values present (all-negative or mixed)
  const range = effectiveMax - effectiveMin;
  const paddedRange = range * 1.1;
  const step = niceNum(paddedRange / desiredTickCount);
  const niceMin = -Math.ceil(Math.abs(effectiveMin * 1.1) / step) * step;
  const niceMax = effectiveMax > 0 ? Math.ceil((effectiveMax * 1.1) / step) * step : 0;

  const ticks: XAxisTick[] = [];
  const tickCount = Math.round((niceMax - niceMin) / step);
  for (let i = 0; i <= tickCount; i++) {
    const rawVal = niceMin + i * step;
    // Round to avoid floating-point artifacts
    const val = Math.round(rawVal * 1e10) / 1e10;
    ticks.push({ value: val, label: formatDisplayValue({ value: val, mode, unit }) });
  }
  return { ticks, axisMin: niceMin, axisMax: niceMax };
}

/**
 * Get threshold line positions for rendering as HTML elements.
 */
export function getThresholdLines({
  thresholds,
  thresholdColors,
  maxValue,
}: {
  thresholds?: ThresholdOptions;
  thresholdColors: ThresholdColorPalette;
  maxValue?: number;
}): Array<{
  value: number;
  color: string;
  label?: string;
  labelColor?: string;
  labelBackgroundColor?: string;
}> {
  if (!thresholds?.steps?.length) return [];
  const steps = thresholds.steps;

  return steps.map((step, index) => {
    const thresholdValue = calculateThresholdStepValue(step, thresholds.mode, maxValue);
    const stepPaletteColor = thresholdColors.palette[index] ?? thresholdColors.defaultColor;
    return {
      value: thresholdValue,
      color: step.color ?? stepPaletteColor,
      label: step.name,
      labelColor: thresholdColors.labelColor,
      labelBackgroundColor: thresholdColors.labelBackgroundColor,
    };
  });
}
